import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import SaveIcon from "@mui/icons-material/Save";
import VrpanoIcon from "@mui/icons-material/Vrpano";
import MenuIcon from "@mui/icons-material/Menu";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import { LessonFormDrawer } from "components/views/editor/components/LessonFormDrawer";
import { VersionSelector } from "components/views/editor/components/VersionSelector";
import { ConfirmNavigationDialog } from "components/views/editor/components/ConfirmNavigationDialog";
import {
    LessonEditorContentBox,
    ActionButtonsBox,
    StyledSpeedDial,
    StyledSpeedDialAction,
    FormLoadingButton
} from "components/views/editor/UpdateTrainingPage.styles";
import { LessonForm } from "components/views/editor/models/LessonForm";
import { LessonsTags } from "models/LessonsTags";
import { LessonFormValidationErrors } from "models/LessonFormValidationErrors";
import { IGetLessonWithVersionsResponse } from "api/models/response/IGetLessonWithVersionsResponse";

export interface UpdateLessonPageLayoutProps {
    lessonData: IGetLessonWithVersionsResponse;
    lessonFormData: LessonForm;
    tags: LessonsTags;
    formValidationErrors: LessonFormValidationErrors;
    currentLanguage: string;
    pendingNewVersion: string | null;
    onFormDataChange: (field: keyof LessonForm, data: LessonForm) => void;
    onLanguageChange: (language: string) => void;
    onRefresh: () => void;
    onAddNewVersion: (language: string) => void;
    onSave: () => Promise<void>;
    isUpdating: boolean;
    isDialogOpen: boolean;
    onCancelDialog: () => void;
    onConfirmDialog: () => void;
    fetchTrainingWithVersionsAndProgressPending: boolean;
    onExportToVrClick: () => void;
    onExportClick: () => void;
    onImportClick?: () => void;
    showImportButton?: boolean;
    showExportFileButton?: boolean;
    children: React.ReactNode;
}

export const UpdateLessonPageLayout = (props: UpdateLessonPageLayoutProps) => {
    const {
        lessonData,
        lessonFormData,
        tags,
        formValidationErrors,
        currentLanguage,
        pendingNewVersion,
        onFormDataChange,
        onLanguageChange,
        onRefresh,
        onAddNewVersion,
        onSave,
        isUpdating,
        isDialogOpen,
        onCancelDialog,
        onConfirmDialog,
        fetchTrainingWithVersionsAndProgressPending,
        onExportToVrClick,
        onExportClick,
        onImportClick,
        showImportButton = false,
        showExportFileButton = true,
        children
    } = props;
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [openSpeedDial, setOpenSpeedDial] = useState(false);
    const showExportFile = showExportFileButton ?? true;

    return (
        <>
        <LessonEditorContentBox id="lesson-editor-content">
            <VersionSelector
                lesson={lessonData}
                currentLanguage={currentLanguage}
                pendingNewVersion={pendingNewVersion}
                onLanguageChange={onLanguageChange}
                onRefresh={onRefresh}
                onAddNewVersion={onAddNewVersion}
            />
            <LessonFormDrawer
                title={t("lessonEditor.editTraining")}
                tags={tags}
                lessonFormData={lessonFormData}
                formValidationErrors={formValidationErrors}
                onLessonDataChange={onFormDataChange}
                disableLessonTypeChange
            />
            <ActionButtonsBox openSpeedDial={openSpeedDial}>
                <FormLoadingButton
                    variant="outlined"
                    color="primary"
                    loading={isUpdating}
                    onClick={() => navigate(-1)}
                >
                    {t("lessonEditor.cancel")}
                </FormLoadingButton>
                <FormLoadingButton
                    variant="contained"
                    loading={isUpdating}
                    onClick={onSave}
                >
                    {t("lessonEditor.save")}
                </FormLoadingButton>
                <StyledSpeedDial
                    ariaLabel=""
                    FabProps={{ size: "small" }}
                    icon={<MenuIcon />}
                    onClose={() => setOpenSpeedDial(false)}
                    onOpen={() => setOpenSpeedDial(true)}
                    open={openSpeedDial}
                >
                    <StyledSpeedDialAction
                        key="speed-dial-export-to-vr"
                        icon={<VrpanoIcon />}
                        tooltipTitle={t("lessonEditor.exportToVrApp")}
                        tooltipOpen
                        FabProps={{ disabled: fetchTrainingWithVersionsAndProgressPending }}
                        onClick={onExportToVrClick}
                    />
                    {showExportFile && (
                        <StyledSpeedDialAction
                            key="speed-dial-export"
                            icon={<SaveIcon />}
                            tooltipTitle={t("lessonEditor.exportFile")}
                            tooltipOpen
                            onClick={onExportClick}
                        />
                    )}
                    {showImportButton && onImportClick && (
                        <StyledSpeedDialAction
                            key="speed-dial-import"
                            icon={<UploadFileIcon />}
                            tooltipTitle={t("lessonEditor.importFile")}
                            tooltipOpen
                            onClick={onImportClick}
                        />
                    )}
                </StyledSpeedDial>
            </ActionButtonsBox>
            {children}
        </LessonEditorContentBox>
        <ConfirmNavigationDialog
            open={isDialogOpen}
            handleCancel={onCancelDialog}
            handleConfirm={onConfirmDialog}
            message={t("nodeEditor.unsavedChanges")}
        />
    </>
    );
};
